import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { mediaBatchService, MediaBatchSummary, buildImageUrl } from '../../services/api';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import WatermarkConfigModal from '../../components/admin/WatermarkConfigModal';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TitleBox = styled.div``;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 0.25rem;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 0;
  max-width: 640px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'ghost' }>`
  padding: 0.7rem 1.4rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  border: ${({ $variant }) => ($variant === 'ghost' ? '1px solid #cbd5e1' : 'none')};
  font-weight: 500;
  background: ${({ $variant, theme }) =>
    $variant === 'secondary' ? '#e2e8f0' : $variant === 'ghost' ? 'white' : theme.colors.primary};
  color: ${({ $variant }) => ($variant === 'primary' || !$variant ? 'white' : '#1e293b')};

  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

const Cover = styled.div<{ $url?: string | null }>`
  height: 150px;
  background: ${({ $url }) =>
    $url ? `center / cover no-repeat url(${$url})` : 'linear-gradient(135deg,#e2e8f0,#cbd5e1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 2rem;
`;

const CardBody = styled.div`
  padding: 1rem 1.25rem;
`;

const CardTitle = styled.h3`
  margin: 0 0 0.25rem;
  color: #1e293b;
  font-size: 1.05rem;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const Badge = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-weight: 600;
`;

const DeleteLink = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: #94a3b8;

  h3 {
    color: #475569;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.4rem;
    font-weight: 500;
    color: #333;
  }
  input,
  textarea {
    width: 100%;
    padding: 0.7rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1rem;
    font-family: inherit;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const AdminMediaBatches: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [batches, setBatches] = useState<MediaBatchSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [watermarkOpen, setWatermarkOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaBatchSummary | null>(null);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mediaBatchService.list();
      setBatches(data);
    } catch (error) {
      showNotification('Erro ao carregar lotes de mídia', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleCreate = async () => {
    if (!name.trim()) {
      showNotification('Informe um nome para o lote', 'error');
      return;
    }
    try {
      setCreating(true);
      const batch = await mediaBatchService.create(name.trim(), description.trim() || undefined);
      showNotification('Lote criado com sucesso!', 'success');
      setCreateOpen(false);
      setName('');
      setDescription('');
      navigate(`/admin/media-batches/${batch.id}`);
    } catch (error) {
      showNotification('Erro ao criar o lote', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await mediaBatchService.remove(deleteTarget.id);
      showNotification('Lote removido', 'success');
      setDeleteTarget(null);
      fetchBatches();
    } catch (error) {
      showNotification('Erro ao remover o lote', 'error');
    }
  };

  return (
    <Container>
      <Header>
        <TitleBox>
          <Title>Mídias em Lote</Title>
          <Subtitle>
            Envie várias fotos de uma vez com marca d'água aplicada automaticamente, baixe todas em
            um arquivo .zip e reutilize as imagens nos imóveis.
          </Subtitle>
        </TitleBox>
        <HeaderActions>
          <Button $variant="ghost" onClick={() => setWatermarkOpen(true)}>
            ⚙️ Configurar marca d'água
          </Button>
          <Button onClick={() => setCreateOpen(true)}>+ Novo lote</Button>
        </HeaderActions>
      </Header>

      {loading ? (
        <EmptyState>Carregando...</EmptyState>
      ) : batches.length === 0 ? (
        <EmptyState>
          <h3>Nenhum lote ainda</h3>
          <p>Crie seu primeiro lote para enviar fotos em massa.</p>
          <Button onClick={() => setCreateOpen(true)}>+ Criar lote</Button>
        </EmptyState>
      ) : (
        <Grid>
          {batches.map((batch) => (
            <Card key={batch.id} onClick={() => navigate(`/admin/media-batches/${batch.id}`)}>
              <Cover $url={batch.cover_url ? buildImageUrl(batch.cover_url) : null}>
                {!batch.cover_url && '🖼️'}
              </Cover>
              <CardBody>
                <CardTitle>{batch.name}</CardTitle>
                {batch.description && (
                  <small style={{ color: '#64748b' }}>{batch.description}</small>
                )}
                <CardMeta>
                  <Badge>{batch.images_count} foto{batch.images_count !== 1 ? 's' : ''}</Badge>
                  <DeleteLink
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(batch);
                    }}
                  >
                    Remover
                  </DeleteLink>
                </CardMeta>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Novo lote de mídia" size="sm">
        <FormField>
          <label>Nome do lote *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Apartamento Jardins - 3 quartos"
            autoFocus
          />
        </FormField>
        <FormField>
          <label>Descrição (opcional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Observações sobre este lote"
          />
        </FormField>
        <ModalActions>
          <Button $variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? 'Criando...' : 'Criar lote'}
          </Button>
        </ModalActions>
      </Modal>

      <WatermarkConfigModal isOpen={watermarkOpen} onClose={() => setWatermarkOpen(false)} />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Remover lote"
        message={`Tem certeza que deseja remover o lote "${deleteTarget?.name}"? As fotos já usadas em imóveis continuam disponíveis.`}
        confirmText="Sim, remover"
        cancelText="Cancelar"
        type="warning"
      />
    </Container>
  );
};

export default AdminMediaBatches;
